import { forkJoin, of, type Observable } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';

import { AlertService, alertMessages } from '../../services/alert.service';
import { JobPostingService } from '../../../api/services/job-posting.service';

import type { FormDefinition } from '../../forms/form.types';
import type { JobPosting } from '../../../api/models/job-posting';
import type { Company } from '../../../api/models/company';
import type { MatchCriterion } from '../../../api/models/match-criterion';

import { buildJobPostingFields } from './job-posting.fields';

// ── Types ───────────────────────────────────────────────────────────────

export interface JobPostingInput {
  entity?: JobPosting;
  profile?: Company;
  isAdminMode: boolean;
}

export type JobPostingModel = Partial<JobPosting> & {
  attachment?: { pendingFile: File | null } | null;
  matchcriterion?: MatchCriterion;
};

// ── Form definition ─────────────────────────────────────────────────────

/**
 * Builds the FormDefinition for a job-posting form. Behaviorless — the caller
 * opens this with FormDialogService and runs `saveJobPosting()` after close.
 */
export function buildJobPostingDefinition(
  input: JobPostingInput,
): FormDefinition<JobPostingModel> {
  const model: JobPostingModel = { ...(input.entity ?? {}) };

  // The companyId field is hidden in non-admin mode (`hide: !isAdminMode`),
  // and Formly strips hidden fields' values from the model on submit. Without
  // this preseed the BE receives a payload without companyId and crashes with
  // a NullReferenceException in JobPostingController.Create. Setting it on
  // the model directly keeps it in the payload regardless of field visibility.
  if (!input.isAdminMode && input.profile?.id && !model.companyId) {
    model.companyId = input.profile.id;
  }

  return {
    titleKey: input.entity?.id ? _('Edit job posting') : _('Add job posting'),
    model,
    fields: buildJobPostingFields(input, model),
  };
}

// ── Save ────────────────────────────────────────────────────────────────

const EMPTY_MATCH: MatchCriterion = {
  companyCultureFactor: 0,
  personalityFactor: 0,
  areaOfInterestFactor: 0,
  companyCultureCompetencies: [],
  personalityCompetencies: [],
};

/**
 * Persists a JobPostingModel: entity create/update, match-criterion upsert,
 * optional pending-file upload. Emits alerts on success/error.
 */
export function saveJobPosting(
  jobs: JobPostingService,
  alert: AlertService,
  model: JobPostingModel,
): Observable<{ id: string }> {
  const { attachment, matchcriterion, ...rest } = model;
  const body = rest as JobPosting;
  const existingId = model.id;
  const entity$: Observable<JobPosting> = existingId
    ? jobs.updateJobPosting({ id: existingId, body })
    : jobs.createJobPosting({ body });

  return entity$.pipe(
    switchMap((res) => {
      const entityId = existingId ?? res?.id ?? '';
      const mc$ = jobs
        .updateJobPostingMatchCriterion({
          jobPostingId: entityId,
          body: matchcriterion ?? EMPTY_MATCH,
        })
        .pipe(catchError(() => of(null)));
      const file = attachment?.pendingFile ?? null;
      // Upload failures shouldn't roll back the entity create/update —
      // surface them to Sentry so we can debug, but let the save succeed.
      const upload$ = file
        ? jobs.uploadJobPostingDocument({ jobPostingId: entityId, body: { file } }).pipe(
            catchError((uploadErr) => {
              alert.errorWithReport(uploadErr, 'saveJobPosting:uploadDocument', {
                jobPostingId: entityId,
              });
              return of(null);
            }),
          )
        : of(null);
      return forkJoin([mc$, upload$]).pipe(map(() => ({ id: entityId })));
    }),
    tap(() => alert.success(alertMessages.successDataSaved)),
    catchError((err) => {
      alert.errorWithReport(err, 'saveJobPosting', {
        jobPostingId: existingId ?? null,
        mode: existingId ? 'update' : 'create',
      });
      throw err;
    }),
  );
}

